import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, X, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lead-Technician / Employee / Superadmin case-edit modal.
 *
 * Edits every mutable field on a case (everything except case_number / clinic
 * identity). Each changed field is logged as an `edit` row in `case_audit`
 * server-side — visible to the operator on the Activity tab.
 *
 * Props:
 *   - caseData : the current case object (used as initial form state)
 *   - onClose  : callback to dismiss the modal
 *   - onSaved  : called with the updated case after PATCH succeeds
 */
export default function CaseEditModal({ caseData, onClose, onSaved }) {
    const c = caseData || {};
    const [form, setForm] = useState({
        patient_id: c.patient_id || "",
        patient_name: c.patient_name || "",
        email: c.email || "",
        dentist_name: c.dentist_name || "",
        phone: c.phone || "",
        product: c.product || "",
        shade: c.shade || "",
        tooth_numbers: Array.isArray(c.tooth_numbers) ? c.tooth_numbers.join(", ") : "",
        notes: c.notes || "",
        assigned_to: c.assigned_to || "",
        order_date: c.order_date || "",
        expected_delivery: c.expected_delivery || "",
        purchase_order_number: c.purchase_order_number || "",
        attached_materials: Array.isArray(c.attached_materials) ? c.attached_materials.join(", ") : "",
        is_backlog: c.is_backlog || false,
    });
    const [saving, setSaving] = useState(false);
    const [techs, setTechs] = useState([]);

    // Load technicians once for the assignee dropdown
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/admin/team`)
            .then(({ data }) => { if (alive) setTechs((data || []).filter((u) => ["technician", "lead_technician", "employee"].includes(u.role) && u.active !== false)); })
            .catch(() => { /* assignee dropdown stays empty; user can still set other fields */ });
        return () => { alive = false; };
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const diffPayload = useMemo(() => {
        const out = {};
        const fields = ["patient_id", "patient_name", "email", "dentist_name", "phone", "product", "shade", "notes", "assigned_to", "order_date", "expected_delivery", "purchase_order_number"];
        fields.forEach((f) => {
            const cur = (c[f] ?? "");
            const next = (form[f] ?? "");
            if (cur !== next) out[f] = next || null;
        });
        // tooth_numbers — comma-separated string ⇄ int[]
        const newTeeth = (form.tooth_numbers || "")
            .split(/[\s,]+/)
            .map((t) => parseInt(t, 10))
            .filter((t) => Number.isFinite(t));
        const curTeeth = Array.isArray(c.tooth_numbers) ? c.tooth_numbers : [];
        if (newTeeth.length !== curTeeth.length || newTeeth.some((t, i) => t !== curTeeth[i])) {
            out.tooth_numbers = newTeeth;
        }
        // attached_materials — comma-separated string ⇄ string[]
        const newMats = (form.attached_materials || "")
            .split(/,/)
            .map((m) => m.trim())
            .filter(Boolean);
        const curMats = Array.isArray(c.attached_materials) ? c.attached_materials : [];
        if (newMats.length !== curMats.length || newMats.some((m, i) => m !== curMats[i])) {
            out.attached_materials = newMats;
        }
        if ((c.is_backlog || false) !== form.is_backlog) out.is_backlog = form.is_backlog;
        return out;
    }, [c, form]);

    const dirtyCount = Object.keys(diffPayload).length;

    const save = async (e) => {
        e?.preventDefault?.();
        if (dirtyCount === 0) {
            toast.info("Nothing changed");
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.patch(`${API}/admin/cases/${c.id}`, diffPayload);
            toast.success(`Saved · ${dirtyCount} field${dirtyCount > 1 ? "s" : ""} updated`);
            onSaved?.(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="case-edit-modal">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl border border-slate-200">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                    <h3 className="font-display text-lg tracking-tight text-slate-900 inline-flex items-center gap-2">
                        <Pencil size={15} className="text-cyan-600" />
                        Edit case {c.case_number}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" aria-label="Close" data-testid="case-edit-close">
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={save} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    <div className="text-[12.5px] text-slate-600 bg-amber-50/60 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
                        Every field you change is recorded in the case&rsquo;s <strong>Activity</strong> timeline (who changed what, when, from what to what).
                        Identity fields (case number, clinic) cannot be edited.
                    </div>

                    {/* Patient info */}
                    <Section title="Patient">
                        <Field label="Patient ID"><input value={form.patient_id} onChange={(e) => set("patient_id", e.target.value)} className="tr-edit-input" data-testid="edit-patient-id" /></Field>
                        <Field label="Patient name"><input value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="tr-edit-input" data-testid="edit-patient-name" /></Field>
                        <Field label="Clinic email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="tr-edit-input" data-testid="edit-email" /></Field>
                        <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="tr-edit-input" data-testid="edit-phone" /></Field>
                        <Field label="Dentist name"><input value={form.dentist_name} onChange={(e) => set("dentist_name", e.target.value)} className="tr-edit-input" data-testid="edit-dentist-name" /></Field>
                    </Section>

                    {/* Clinical */}
                    <Section title="Clinical">
                        <Field label="Product (slug)"><input value={form.product} onChange={(e) => set("product", e.target.value)} className="tr-edit-input" data-testid="edit-product" /></Field>
                        <Field label="Shade"><input value={form.shade} onChange={(e) => set("shade", e.target.value)} className="tr-edit-input" data-testid="edit-shade" /></Field>
                        <Field label="Tooth numbers (Universal · comma sep)" full>
                            <input value={form.tooth_numbers} onChange={(e) => set("tooth_numbers", e.target.value)} className="tr-edit-input" placeholder="e.g. 8, 9, 10" data-testid="edit-tooth-numbers" />
                        </Field>
                        <Field label="Attached materials (comma sep)" full>
                            <input value={form.attached_materials} onChange={(e) => set("attached_materials", e.target.value)} className="tr-edit-input" placeholder="impression, bite, opposing cast" data-testid="edit-materials" />
                        </Field>
                        <Field label="Clinical notes" full>
                            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="tr-edit-input" data-testid="edit-notes" />
                        </Field>
                    </Section>

                    {/* Workflow */}
                    <Section title="Workflow">
                        <Field label="Order date (YYYY-MM-DD)"><input value={form.order_date} onChange={(e) => set("order_date", e.target.value)} placeholder="2026-06-08" className="tr-edit-input" data-testid="edit-order-date" /></Field>
                        <Field label="Expected delivery (YYYY-MM-DD)"><input value={form.expected_delivery} onChange={(e) => set("expected_delivery", e.target.value)} placeholder="2026-06-12" className="tr-edit-input" data-testid="edit-expected-delivery" /></Field>
                        <Field label="Clinic PO #"><input value={form.purchase_order_number} onChange={(e) => set("purchase_order_number", e.target.value)} className="tr-edit-input" data-testid="edit-po-number" /></Field>
                        <Field label="Assignee">
                            <select value={form.assigned_to || ""} onChange={(e) => set("assigned_to", e.target.value)} className="tr-edit-input" data-testid="edit-assignee">
                                <option value="">— Unassigned —</option>
                                {techs.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name || t.email} · {t.role.replace("_", " ")}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Backlog flag" full>
                            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer text-[13px]">
                                <input type="checkbox" checked={!!form.is_backlog} onChange={(e) => set("is_backlog", e.target.checked)} className="accent-cyan-600" data-testid="edit-is-backlog" />
                                <span>Treat as backlog (skip SLA + auto-balance)</span>
                            </label>
                        </Field>
                    </Section>
                </form>

                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
                    <span className="text-[12px] text-slate-600 font-mono-tr tracking-wide" data-testid="case-edit-dirty-count">
                        {dirtyCount === 0 ? "No changes yet" : `${dirtyCount} field${dirtyCount > 1 ? "s" : ""} pending`}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-100">Cancel</button>
                        <button
                            type="submit"
                            onClick={save}
                            disabled={saving || dirtyCount === 0}
                            data-testid="case-edit-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-700 hover:bg-cyan-800 text-white text-[13px] font-medium disabled:opacity-50"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            Save &amp; log to audit
                        </button>
                    </div>
                </div>
            </div>
            <style>{`.tr-edit-input{width:100%;padding:.5rem .75rem;border:1px solid #e2e8f0;border-radius:8px;background:#fff;font-size:13px;color:#0f172a;outline:none;}.tr-edit-input:focus{border-color:#06b6d4;box-shadow:0 0 0 3px rgba(6,182,212,.15);}`}</style>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">· {title}</div>
            <div className="grid sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function Field({ label, children, full }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="block text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}
